import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../../components/common/PageHeader';
import { Link } from 'react-router-dom';
import { FaSeedling, FaWater, FaTractor, FaExchangeAlt } from 'react-icons/fa';

const AgriculturalFundPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Fund', path: '/fund' },
    { label: 'Agricultural Fund' }
  ];

  const investmentAreas = [
    {
      icon: <FaSeedling className="w-8 h-8 text-amber-600" />,
      title: "Sustainable Farming",
      description: "Investments in high-yield sustainable farming operations that combine traditional knowledge with modern techniques."
    },
    {
      icon: <FaWater className="w-8 h-8 text-blue-500" />,
      title: "Agricultural Technology",
      description: "Funding for innovative agricultural technologies including precision farming, smart irrigation, and crop monitoring systems."
    },
    {
      icon: <FaTractor className="w-8 h-8 text-yellow-600" />,
      title: "Land & Infrastructure",
      description: "Strategic acquisitions of premium agricultural land and investment in critical rural infrastructure projects."
    },
    {
      icon: <FaExchangeAlt className="w-8 h-8 text-purple-500" />,
      title: "Supply Chain Integration",
      description: "Developing integrated supply chain solutions to maximize value creation from farm to market."
    }
  ];

  return (
    <>
      <PageHeader 
        title="Agricultural Fund" 
        breadcrumbs={breadcrumbs}
      />
      
      <section className="py-16 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary">
                Investing in Agricultural Innovation and Sustainability
              </h2>
              <p className="text-lg text-gray-700">
                The Blockfirst Capital Agricultural Fund provides investors with access to carefully selected opportunities in the agriculture sector, focusing on sustainable farming operations, agricultural technology, and infrastructure.
              </p>
              <p className="text-lg text-gray-700">
                Our fund delivers a unique combination of potential for competitive financial returns, portfolio diversification benefits, and positive environmental impact through sustainable agricultural practices.
              </p>
              <div className="pt-4">
                <Link 
                  to="/contact-us" 
                  className="px-8 py-3 bg-secondary hover:bg-secondary-dark text-white font-semibold rounded-lg transition-colors inline-block"
                >
                  Learn About Investment Opportunities
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/farmer-standing-corn-field-examining-crop-harvest-care-concept-agriculture-ecology-concept_217236-25520.jpg_eiataa.jpg" 
                alt="Agricultural Investment" 
                className="rounded-xl shadow-lg w-full h-auto object-cover"
              />
            </motion.div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-4">
              Investment Areas
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our balanced approach to agricultural investing spans multiple areas, creating a diversified portfolio with exposure to different aspects of the agricultural value chain.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {investmentAreas.map((area, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="mb-4">{area.icon}</div>
                <h4 className="text-xl font-heading font-semibold mb-3">{area.title}</h4>
                <p className="text-gray-600">{area.description}</p>
              </motion.div>
            ))}
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <img 
                src="/assets/man-writing-clipboard-wheat-field_23-2148233467.jpg_oalaw6.jpg" 
                alt="Agricultural Planning" 
                className="rounded-xl shadow-lg w-full h-auto object-cover"
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="space-y-6 order-1 lg:order-2"
            >
              <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary">
                Investment Strategy
              </h3>
              <p className="text-lg text-gray-700">
                Our approach to agricultural investment is built on strategic pillars that guide our decision-making process and portfolio construction.
              </p>
              
              <div className="space-y-4 mt-6">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h4 className="text-xl font-heading font-semibold mb-2 text-primary">Geographical Diversification</h4>
                  <p className="text-gray-600">Portfolio diversification across multiple regions and climate zones to minimize environmental and geopolitical risks.</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h4 className="text-xl font-heading font-semibold mb-2 text-primary">Crop Variety Strategy</h4>
                  <p className="text-gray-600">Balanced exposure to different crop types, including staples, specialty crops, and high-value produce.</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h4 className="text-xl font-heading font-semibold mb-2 text-primary">Technology Integration</h4>
                  <p className="text-gray-600">Leveraging agricultural technology to improve yields, reduce resource usage, and increase operational efficiency.</p>
                </div>
              </div>
            </motion.div>
          </div>
          
          <div className="mt-16 bg-amber-900 text-white rounded-xl p-10 shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-amber-400 mb-2">$85M+</div>
                <div className="text-gray-300">Assets Under Management</div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-amber-400 mb-2">12,500</div>
                <div className="text-gray-300">Hectares Under Management</div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-amber-400 mb-2">8</div>
                <div className="text-gray-300">Countries</div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-amber-400 mb-2">16.2%</div>
                <div className="text-gray-300">Average Annual Return</div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AgriculturalFundPage;
